import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { css } from "glamor";

import { useUi } from "../../../pages/Concierge/ui";
import Field from "../../../pages/Concierge/vip/components/Field";
import VipContactPicker from "../../../pages/Concierge/dashboard/components/VipContactPicker";
import { T } from "../../../pages/Concierge/constants";
import { PERMISSIONS } from "../../../lib/authManager";
import { PermissionOnly } from "../../../components/AdminOnly";
import { isPlateInLot } from "../RateCards/queries";
import drivoApollo from "../../../lib/drivoApollo";
import { useIsVisible } from "react-is-visible";

const useStyles = makeStyles({
    row: {
        padding: 20,
        borderTop: `1px solid ${T.borderSoft}`,
        "&:first-child": { borderTop: "none" },
    },
    fields: {
        display: "grid",
        gridTemplateColumns: "1.1fr 1.4fr 1fr",
        gap: 12,
        "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
    },
    description: { marginTop: 12 },
    contacts: { marginTop: 12 },
    caption: {
        display: "block",
        marginBottom: 4,
        color: T.mutedFg,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
    },
    status: { display: "flex", alignItems: "center", gap: 24, marginTop: 12, flexWrap: "wrap" },
    statusField: { minWidth: 140 },
    readOnly: { color: T.mutedFg, fontSize: 13 },
    actions: { display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 16 },
    statusDot: { display: "inline-block", width: 8, height: 8, borderRadius: "50%", marginRight: 6 },
    secondaryButton: {
        borderRadius: 6,
        border: `1px solid ${T.border}`,
        background: "transparent",
        color: T.mutedFg,
        fontSize: 13,
        fontFamily: "inherit",
        padding: "8px 12px",
        cursor: "pointer",
        "&:hover": { background: T.surface },
        "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
    },
    deleteButton: {
        borderRadius: 6,
        border: `1px solid ${T.border}`,
        background: "transparent",
        color: T.mutedFg,
        fontSize: 13,
        fontFamily: "inherit",
        padding: "8px 12px",
        cursor: "pointer",
        "&:hover": { borderColor: T.danger, color: T.danger },
        "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
    },
    dangerButton: {
        borderRadius: 6,
        border: `1px solid ${T.danger}`,
        background: T.danger,
        color: T.primaryFg,
        fontSize: 13,
        fontFamily: "inherit",
        padding: "8px 12px",
        cursor: "pointer",
    },
});

const PIN_STATUS_COLOURS = {
    provisioned: '#2a8a3a',
    pending: '#c08a18',
    failed: '#c0392b',
    revoked: '#8a8a8a',
};

// Keep this outside PlateRow. Search filtering unmounts rows, so a per-row
// ref is lost and the same plate would otherwise query its status again when
// it is rendered for the next search result.
const plateLotStatusCache = new Map();
const PLATE_STATUS_CACHE_TTL = 30000;

function getPlateLotStatus(facilityId, plate) {
    const cacheKey = `${facilityId}:${plate}`;
    const cached = plateLotStatusCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) return cached.promise;
    if (cached) plateLotStatusCache.delete(cacheKey);

    const request = drivoApollo.query({
        query: isPlateInLot,
        fetchPolicy: 'cache-first',
        variables: { id: facilityId, plate },
    }).then(({ data }) => {
        const status = data?.facility?.isPlateInLot;
        if (typeof status === 'boolean') {
            plateLotStatusCache.set(cacheKey, {
                promise: Promise.resolve(status),
                expiresAt: Date.now() + PLATE_STATUS_CACHE_TTL,
            });
        } else {
            plateLotStatusCache.delete(cacheKey);
        }
        return status;
    }).catch((error) => {
        plateLotStatusCache.delete(cacheKey);
        throw error;
    });

    plateLotStatusCache.set(cacheKey, { promise: request, expiresAt: Infinity });
    return request;
}

const PinpadStatus = ({ pinpadAccess }) => {
    if (!pinpadAccess) return <span className={css({ color: '#bbb' })}>—</span>;
    const colour = PIN_STATUS_COLOURS[pinpadAccess.status] || '#8a8a8a';
    return (
        <span style={{ color: colour }} title={pinpadAccess.lastError || undefined}>
            {pinpadAccess.pin || pinpadAccess.status}
        </span>
    );
};

function initialContactIds(plate) {
    return plate.vipContacts?.length
        ? plate.vipContacts.map(({ contactId }) => contactId).filter(Boolean)
        : (plate.vipContactId ? [plate.vipContactId] : []);
}

const PlateRowComponent = ({
    plate,
    lotsOptions = [],
    contacts = [],
    deleting,
    showPin,
    canManage,
    onSave,
    onDelete,
    onDeleteConfirm,
    onDeleteCancel,
    facilityId,
}) => {
    const s = useStyles();
    const ui = useUi();
    const nodeRef = React.useRef();
    const isVisible = useIsVisible(nodeRef);
    const makeDraft = () => ({
        plate: plate.plate || "",
        label: plate.label || "",
        lotId: plate.lot?.id || "",
        isVip: plate.isVip,
        isEnabled: plate.isEnabled,
        vipContactIds: initialContactIds(plate),
    });
    const draftSourceKey = JSON.stringify(makeDraft());
    const [draft, setDraft] = useState(makeDraft);
    const [submitting, setSubmitting] = useState(false);
    const [isPlateInLotStatus, setIsPlateInLotStatus] = useState(null);
    const draftSourceKeyRef = useRef(draftSourceKey);
    useEffect(() => {
        if (draftSourceKeyRef.current === draftSourceKey) return;
        draftSourceKeyRef.current = draftSourceKey;
        setDraft(makeDraft());
    }, [draftSourceKey]);

    useEffect(() => {
        if (!isVisible) return undefined;
        let active = true;
        getPlateLotStatus(facilityId, plate.plate)
            .then((status) => {
                if (active && typeof status === 'boolean') setIsPlateInLotStatus(status);
            })
            .catch(() => undefined);
        return () => { active = false; };
    }, [isVisible, facilityId, plate.id, plate.plate]);

    const isDirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(makeDraft()), [draft, plate]);

    const updateField = (field) => (event) => {
        const value = field === "isVip" || field === "isEnabled" ? event.target.checked : event.target.value;
        setDraft((current) => ({ ...current, [field]: value }));
    };

    const save = async (event) => {
        event.preventDefault();
        if (!draft.plate.trim() || !isDirty || submitting) return;
        setSubmitting(true);
        try {
            await onSave({
                id: plate.id,
                ...draft,
                lotId: draft.lotId || null,
                vipContactIds: draft.isVip ? draft.vipContactIds : [],
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <li ref={nodeRef} className={s.row}>
            <form onSubmit={save}>
                <div className={s.fields}>
                    <Field label="Licence plate">
                        <input className={ui.field} disabled={!canManage || submitting} value={draft.plate} onChange={updateField("plate")} />
                    </Field>
                    <Field label="Description">
                        <input className={ui.field} disabled={!canManage || submitting} value={draft.label} onChange={updateField("label")} />
                    </Field>
                    <Field label="Lot">
                        <select className={ui.field} disabled={!canManage || submitting} value={draft.lotId} onChange={updateField("lotId")}>
                            <option value="">Default</option>
                            {lotsOptions.map((lot) => (
                                <option key={lot.id} value={lot.id}>{lot.name}{lot.description ? ` (${lot.description})` : ""}</option>
                            ))}
                        </select>
                    </Field>
                </div>

                <div className={s.status}>
                    <label className={css({ color: T.fg, display: "flex", alignItems: "center", gap: 8, fontSize: 14 })}>
                        <input type="checkbox" checked={draft.isVip} disabled={!canManage || submitting} onChange={updateField("isVip")} />
                        VIP
                    </label>
                    <label className={css({ color: T.fg, display: "flex", alignItems: "center", gap: 8, fontSize: 14 })}>
                        <input type="checkbox" checked={draft.isEnabled} disabled={!canManage || submitting} onChange={updateField("isEnabled")} />
                        Active
                    </label>
                    {showPin && <div className={s.statusField}>
                        <span className={s.caption}>PINpad</span>
                        <span className={s.readOnly}><PinpadStatus pinpadAccess={plate.pinpadAccess} /></span>
                    </div>}
                    <div className={s.statusField}>
                        <span className={s.caption}>In lot</span>
                        <span className={s.readOnly}>
                            <span
                                className={s.statusDot}
                                style={{ backgroundColor: isPlateInLotStatus ? T.success : T.warningSolid }}
                            />
                            {isPlateInLotStatus === null ? "Checking…" : isPlateInLotStatus ? "In lot" : "Not in lot"}
                        </span>
                    </div>
                </div>

                {draft.isVip && <div className={s.contacts}>
                    <span className={s.caption}>VIP contacts</span>
                    <VipContactPicker
                        contacts={contacts}
                        value={draft.vipContactIds}
                        onChange={(vipContactIds) => setDraft((current) => ({ ...current, vipContactIds }))}
                        requireReachable={false}
                        showChannels={false}
                        emptyMessage="No sanctioned-plate contacts have been added yet."
                        disabled={!canManage || submitting}
                    />
                </div>}

                {!deleting ? (
                    <PermissionOnly permissions={PERMISSIONS.SANCTIONED_PLATES_MANAGE}>
                        <div className={s.actions}>
                            <button type="button" className={s.deleteButton} disabled={submitting} onClick={() => onDelete(plate.id)}>
                                Delete
                            </button>
                            <button type="submit" className={ui.primaryBtn} disabled={!isDirty || submitting}>
                                Save
                            </button>
                        </div>
                    </PermissionOnly>
                ) : (
                    <div className={s.actions}>
                        <button type="button" className={s.dangerButton} onClick={() => onDeleteConfirm(plate.id)}>
                            Confirm delete
                        </button>
                        <button type="button" className={s.secondaryButton} onClick={onDeleteCancel}>
                            Cancel
                        </button>
                    </div>
                )}
            </form>
        </li>
    );
};

/*
 * The list re-renders on every search keystroke, delete toggle and poll.
 * Memoising the row keeps that to the rows whose plate or state changed;
 * the parent passes stable handlers and the row supplies its own plate id.
 */
export const PlateRow = memo(PlateRowComponent);
